import { apiClient, fetchWrapper, getRequestHeaders, IBaseResponse } from './apiBase';

import { IOrganization, IOrganizationIntegration, TOrganizationIntegrationPayload } from '@/types';
import { TMemberRole } from './organizationMembersApi';
import { BASE_API_URL } from '@/config/environment';

export interface IOrganizationResponse extends IBaseResponse {
  access_level: TMemberRole;
  organization?: IOrganization;
}

interface IIntegrationResponse extends IBaseResponse {
  integrations: IOrganizationIntegration[];
}

export interface ICreateParams {
  name: string;
  token?: string;
}

export const createOrganization = async ({ name, token }: ICreateParams): Promise<IOrganizationResponse> => {
  const requestHeaders = getRequestHeaders(token);
  const postData: Object = { name: name };

  try {
    const res = await apiClient.post('/orgs', postData, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

interface IDeleteParams {
  name: string;
  token?: string;
}

export const deleteOrganization = async ({ name, token }: IDeleteParams): Promise<IOrganizationResponse> => {
  const requestHeaders = getRequestHeaders(token);
  try {
    const res = await apiClient.delete(`/orgs/${name}`, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export interface IUpdateParams {
  orgname: string;
  name: string;
  description?: string | null;
  display_name?: string | null;
  image?: any | null;
  token?: string;
}

export const updateOrganization = async ({
  orgname,
  name,
  display_name,
  description,
  image,
  token,
}: IUpdateParams): Promise<IOrganizationResponse> => {
  console.log('updateOrganization', { orgname, name, display_name, description, image, token });
  const resourceUrl = `/orgs/${orgname}`;
  const requestHeaders = getRequestHeaders(token);
  requestHeaders['Content-Type'] = 'multipart/form-data';

  const formData = new FormData();
  formData.append('name', name);
  formData.append('description', description || '');
  if (display_name) {
    formData.append('display_name', display_name);
  }
  if (image) {
    formData.append('image', image);
  }

  try {
    const res = await apiClient.patch(resourceUrl, formData, { headers: requestHeaders });

    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

const mapAccessLevelToOrganization = (response: IOrganizationResponse): IOrganizationResponse => {
  return {
    ...response,
    organization: {
      ...response.organization,
      access_level: response.access_level,
      entityType: 'organization',
    } as IOrganization,
  };
};

export const fetchOrganization = async (orgname: string, token?: string): Promise<IOrganizationResponse> => {
  const targetUrl = `${BASE_API_URL}/orgs/${orgname}`;

  const data = await fetchWrapper({
    resourceName: 'organization',
    targetUrl,
    authToken: token,
    resourceContext: { orgname },
  });
  return mapAccessLevelToOrganization(data);
};

export const fetchIntegrations = async (orgname: string, token?: string): Promise<IIntegrationResponse> => {
  const targetUrl = `${BASE_API_URL}/orgs/${orgname}/integrations`;

  const data = await fetchWrapper({
    resourceName: 'organization',
    targetUrl,
    authToken: token,
    resourceContext: { orgname },
  });

  return data;
};

interface ICreateIntegrationParams {
  orgname: string;
  type: string;
  payload: TOrganizationIntegrationPayload;
  token?: string;
}

export const createOrganizationIntegration = async ({
  orgname,
  type,
  payload,
  token,
}: ICreateIntegrationParams): Promise<IOrganizationResponse> => {
  const targetUrl = `${BASE_API_URL}/orgs/${orgname}/integrations`;
  const requestHeaders = getRequestHeaders(token);
  const body = {
    type,
    payload,
  };

  try {
    const res = await apiClient.post(targetUrl, body, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

interface IUpdateIntegrationParams {
  integration: IOrganizationIntegration;
  orgname: string;
  payload: TOrganizationIntegrationPayload;
  token?: string;
}

export const updateOrganizationIntegration = async ({
  integration,
  orgname,
  payload,
  token,
}: IUpdateIntegrationParams): Promise<IOrganizationResponse> => {
  const targetUrl = `${BASE_API_URL}/orgs/${orgname}/integrations/${integration.id}`;
  const requestHeaders = getRequestHeaders(token);
  const body = {
    ...integration,
    payload,
  };

  try {
    const res = await apiClient.put(targetUrl, body, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export const getOrganization = async (
  orgname: string,
  token?: string | undefined,
): Promise<IOrganizationResponse | undefined> => {
  const resourceUrl = `/orgs/${orgname}`;
  const requestHeaders = getRequestHeaders(token);
  try {
    const res = await apiClient.get(resourceUrl, { headers: requestHeaders });
    return mapAccessLevelToOrganization(res.data);
  } catch (error: any) {
    return error.response.data;
  }
};
